import { useEffect, useRef } from "react";
import { useFlag } from "@unleash/proxy-client-react";
import { useSubscribeChatMessagesQuery } from '../../redux/services/chatApi';
import {
    useGetAvailableTagsQuery,
    useGetInterTenantsQuery,
    useGetSmartDiallerDevicesQuery,
    useGetVoicemailDetailsQuery
} from '../../redux/services/sipApi';
import { useTypedDispatch, useTypedSelector } from '../../redux/hooks';
import {
    addOneApiOnlyPhonebook,
    connectSip,
    deleteSelectedPhonebookContacts,
    getCallerIds,
    getCallHistory,
    getGlobalSettings,
    getParkingSlots,
    getPhonebookContacts,
    parseGlobalSettingsForApi,
    selectCurrentUserId,
    selectGlobalSettings,
    selectGlobalSettingUpdated,
    selectProvisionPhones,
    selectSelectedPhonebooks,
    selectSipCredentials,
    setChatType,
    setGlobalSettingsUpdates,
    updateActivePage,
    updatePhoneSettings,
    updateSelectedPhonebook,
    updateSettingsPage
} from '../../redux/slices';
import { ICRMIntegration, IPhonebook, IUser } from '../../types';
import { useGetUserBlfConfigQuery } from "../../redux/services/provisionApi";
import { authApi, useGetPauseConfigQuery, usePutUserSettingsMutation } from "../../redux/services/authApi";
import { FLAG_NEBULA_CHAT } from "../../helpers";
import { nebulaChatApi } from "../../redux/services/nebulaChat";

/**
 * Chat Details:
 *
 * The way I implemented it is not so clean.
 *
 * We are going to do a debounce of 250ms such that the flag has way more than
 * enough time for an initial load.
 * We then allow usage for flicking the flag after that, this is just to
 * debounce the initial load a bit to not double up api calls.
 */
export const useInitialiseApp = ({ setIsLoaded }: { setIsLoaded: (val: boolean) => void }) => {
    const isNebulaChat = useFlag(FLAG_NEBULA_CHAT);

    const dispatch = useTypedDispatch();

    const isNebulaChatRef = useRef(isNebulaChat);
    const isInitialized = useRef(false);

    useEffect(() => {
        isNebulaChatRef.current = isNebulaChat;
    }, [isNebulaChat]);

    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(setChatType(isNebulaChatRef.current ? 1 : 2));
            isInitialized.current = true;
        }, 250);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isInitialized.current) return;
        dispatch(setChatType(isNebulaChat ? 1 : 2));
        dispatch({ type: 'auth/reset_chat' });
    }, [isNebulaChat]);

    const selectedIntegrationUuids: string[] = useTypedSelector(
        state => state.user.settings.phone.settings?.crmIntegrationUuids
    );
    const integrations: ICRMIntegration[] =
        useTypedSelector(state => state.auth.integrations) || [];
    const authUsers = useTypedSelector(state => state.auth.users); // comes from sip/connect
    const user: IUser = useTypedSelector(state => state.user);
    const hardwarePhonesLength = useTypedSelector(selectProvisionPhones).length;
    const sipCredentials = useTypedSelector(selectSipCredentials);

    // Sip Selectors
    const parkingSlotsLoading = useTypedSelector(state => state.sip.parkingSlotsLoading);
    const phonebookContactsLoading = useTypedSelector(state => state.auth.phonebookContactsLoading);
    const callHistoryLoading = useTypedSelector(state => state.sip.callHistoryLoading);
    const callerIdsLoading = useTypedSelector(state => state.sip.callerIdsLoading);

    const chatType = useTypedSelector(state => state.app.chatType);

    useGetUserBlfConfigQuery(user.uuid, { skip: !user || hardwarePhonesLength < 1 });
    useGetSmartDiallerDevicesQuery(user.uuid, { skip: !user });

    useSubscribeChatMessagesQuery(null, { skip: !authUsers || !user || chatType !== 2 });
    nebulaChatApi.endpoints.connectNebulaChat.useQuery({ userName: user.name, password: user.password, userUuid: user.uuid }, { skip: chatType !== 1 });

    useGetAvailableTagsQuery(null)
    useGetPauseConfigQuery();

    useGetInterTenantsQuery(user.uuid);
    useGetVoicemailDetailsQuery({ voip_user_uuid: user.uuid });

    useEffect(() => {
        if ((window as any).electron) {
            (window as any).electron.receive('open-audio-settings', () => {
                dispatch(updateActivePage('settings'));
                dispatch(updateSettingsPage('audio'));
            });
        }

        if (window?.location.pathname.includes('auth-azure')) {
            window.history.pushState('', '', '/');
        }

        setIsLoaded(true);
    }, []);

    useEffect(() => {
        if (!integrations || integrations.length < 1) return;
        const crmPopIntegrations =
            integrations?.filter(integration => integration.supports_screen_pop) || [];

        selectedIntegrationUuids.forEach(integrationUuid => {
            if (!crmPopIntegrations.some(i => i.uuid === integrationUuid)) {
                dispatch(
                    updatePhoneSettings({
                        setting: 'crmIntegrationUuids',
                        value: selectedIntegrationUuids?.filter(uuid => uuid !== integrationUuid)
                    })
                );
            }
        });
    }, [integrations.length, selectedIntegrationUuids]);

    // SIP RELATED LOGIC
    useEffect(() => {
        if (sipCredentials) {
            dispatch(connectSip(sipCredentials));
        }
    }, [sipCredentials, sipCredentials?.username]);

    useEffect(() => {
        if (user.name && ['idle'].includes(callHistoryLoading)) {
            dispatch(getCallHistory({ username: user.name, week: 1 }));
        }

        if (user.uuid && ['idle'].includes(callerIdsLoading)) {
            dispatch(getCallerIds(user.uuid));
        }

        if (['idle'].includes(parkingSlotsLoading)) {
            dispatch(getParkingSlots());
        }
    }, [
        dispatch,
        user,
        phonebookContactsLoading,
        callHistoryLoading,
        callerIdsLoading,
        parkingSlotsLoading
    ]);

    const emisToken = integrations?.find(i => i.name === 'EMIS')?.uuid;
    authApi.endpoints.tryEmisLogin.useQuery({ voip_user_uuid: user.uuid, token_uuid: emisToken || "" }, { skip: !emisToken });
};

export const useGlobalSettings = () => {
    const userUuid = useTypedSelector(selectCurrentUserId);
    const allowedPhonebooks = useTypedSelector(state => state.user.allowed_phone_books) || [];
    const phonebooksRestricted = useTypedSelector(state => state.user.restrict_phone_books);
    const selectedPhonebooks = useTypedSelector(selectSelectedPhonebooks);
    const globalSettingUpdated = useTypedSelector(selectGlobalSettingUpdated);
    const globalSettings = useTypedSelector(selectGlobalSettings);
    const allPhonebooks: IPhonebook[] = useTypedSelector(state => state.auth.phonebooks);

    const dispatch = useTypedDispatch();

    const [putUserSettings] = usePutUserSettingsMutation();

    const phonebookLock = useRef(false);

    const commitPhonebooks = (uuids: string[]) => {
        const cur = uuids.pop();
        const pb = allPhonebooks?.find(p => p.uuid === cur);

        if (pb) {
            if (pb.total_contacts > 2500) {
                dispatch(addOneApiOnlyPhonebook(pb.uuid));
                commitPhonebooks(uuids);
            } else {
                dispatch(getPhonebookContacts({ phonebookUuid: pb.uuid }));
                commitPhonebooks(uuids);
            }
        }
    };

    useEffect(() => {
        if (
            phonebookLock.current ||
            !selectedPhonebooks ||
            Object.keys(selectedPhonebooks).length < 1 ||
            !allPhonebooks
        )
            return;

        const validatedPhonebooks: string[] = [];

        Object.entries(selectedPhonebooks).forEach(([uuid, val]) => {
            switch (true) {
                case !val:
                    break;
                case !allPhonebooks.some(pb => pb.uuid === uuid):
                case phonebooksRestricted && !allowedPhonebooks.includes(uuid):
                    dispatch(deleteSelectedPhonebookContacts(uuid));
                    dispatch(updateSelectedPhonebook(uuid, false));
                    break;
                case !phonebooksRestricted || allowedPhonebooks.includes(uuid):
                    // dispatch(getPhonebookContacts({phonebookUuid: uuid}));
                    validatedPhonebooks.push(uuid);
                    break;
            }
        });

        commitPhonebooks([...validatedPhonebooks]);

        phonebookLock.current = true;
    }, [selectedPhonebooks, allPhonebooks?.length]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        /**
         * If global setting are updated, wait 1 second before updating them in the API
         */

        if (globalSettingUpdated) {
            timeout = setTimeout(() => {
                putUserSettings({
                    uuid: userUuid,
                    newAipGlobalSettings: parseGlobalSettingsForApi(globalSettings)
                }).then(() => {
                    dispatch(setGlobalSettingsUpdates(false));
                });
            }, 1000);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [globalSettingUpdated]);

    const globalSettingsLock = useRef(false);

    useEffect(() => {
        if (!userUuid || globalSettingsLock.current) return;

        globalSettingsLock.current = true;
        dispatch(getGlobalSettings());
    }, [userUuid]);
};
